import { FormLayout, Input, Label } from "@/components/Forms";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import Text from "@/components/Text";
import { ensureStringContainsPositiveDigitsOnly } from "@/utils/helpers";
import { ChangeEvent, FC } from "react";
import { Control, useController } from "react-hook-form";
import styled from "styled-components";
import { FormDataT } from "./ExtractorForm";

const InputGroup = styled.div`
  position: relative;
`;

const InputLabel = styled(Text)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 38px;
  height: 16px;
  margin: auto 0;
`;

const Buttons = styled.div`
  position: absolute;
  width: 46px;
  right: 0;
  top: 0;
  bottom: 0;
  height: 100%;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: 0.2s ease-out border-color;
`;

const ArrowButton = styled.button`
  width: 8px;
  height: 5px;
  padding: 2px;
  box-sizing: content-box;
  display: flex;
`;

const StyledInput = styled(Input)`
  &:focus,
  &:hover {
    + ${Buttons} {
      border-color: ${({ theme }) => theme.colors.gray};
    }
  }
`;

const StyledFormLayout = styled(FormLayout)`
  grid-column-gap: 16px;
`;

interface Props {
  control: Control<FormDataT>;
  name: "time_from" | "time_to";
  label: string;
}

const DurationField: FC<Props> = ({ control, name, label }) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController<FormDataT, typeof name>({
    control,
    name,
  });

  const minutes = Math.floor(value / 60);
  const seconds = value - minutes * 60;

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!ensureStringContainsPositiveDigitsOnly(e.target.value)) return;

    if (e.target.name === "min") {
      return onChange(parseInt(e.target.value || "0") * 60 + seconds);
    }

    onChange(parseInt(e.target.value || "0") + minutes * 60);
  };

  const increment = (key: "min" | "sec") => {
    if (key === "min") {
      return onChange((minutes + 1) * 60 + seconds);
    }

    onChange(minutes * 60 + seconds + 1);
  };

  const decrement = (key: "min" | "sec") => {
    if (key === "min") {
      if (minutes <= 1) {
        return onChange(seconds);
      }
      return onChange((minutes - 1) * 60 + seconds);
    }
    if (seconds <= 1) {
      return onChange(minutes * 60);
    }

    onChange(minutes * 60 + seconds - 1);
  };

  return (
    <div>
      <>
        <Label>{label}</Label>
        <StyledFormLayout>
          <InputGroup>
            <StyledInput
              onChange={onInputChange}
              name="min"
              value={minutes}
              $small
            />
            <Buttons>
              <ArrowButton type="button" onClick={() => increment("min")}>
                <svg
                  width="8"
                  height="5"
                  viewBox="0 0 8 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M4 0L0 5H8L4 0Z" fill="currentColor" />
                </svg>
              </ArrowButton>
              <ArrowButton type="button" onClick={() => decrement("min")}>
                <svg
                  width="8"
                  height="5"
                  viewBox="0 0 8 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M4 5L8 0H0L4 5Z" fill="currentColor" />
                </svg>
              </ArrowButton>
            </Buttons>
            <InputLabel $color="gray" $type="smallHeadline" as="span">
              min
            </InputLabel>
          </InputGroup>
          <InputGroup>
            <StyledInput
              onChange={onInputChange}
              name="sec"
              value={seconds}
              $small
            />
            <Buttons>
              <ArrowButton type="button" onClick={() => increment("sec")}>
                <svg
                  width="8"
                  height="5"
                  viewBox="0 0 8 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M4 0L0 5H8L4 0Z" fill="currentColor" />
                </svg>
              </ArrowButton>
              <ArrowButton type="button" onClick={() => decrement("sec")}>
                <svg
                  width="8"
                  height="5"
                  viewBox="0 0 8 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M4 5L8 0H0L4 5Z" fill="currentColor" />
                </svg>
              </ArrowButton>
            </Buttons>
            <InputLabel $color="gray" $type="smallHeadline" as="span">
              sec
            </InputLabel>
          </InputGroup>
        </StyledFormLayout>
        {!!error && <ValidationMessage>{error.message}</ValidationMessage>}
      </>
    </div>
  );
};

export default DurationField;
